import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { getPartners } from '@/lib/api'
import { HeartHandshake, Phone, Mail, Clock } from 'lucide-react'
import { useState } from 'react'

const partnerTypes = [
  { value: '', label: 'All Types' },
  { value: 'ngo', label: 'NGO' },
  { value: 'legal', label: 'Legal' },
  { value: 'medical', label: 'Medical' },
  { value: 'shelter', label: 'Shelter' },
  { value: 'federal', label: 'Federal' },
  { value: 'hotline', label: 'Hotline' },
]

export default function Partners() {
  const [typeFilter, setTypeFilter] = useState('')

  const { data: partners, isLoading } = useQuery({
    queryKey: ['partners', typeFilter],
    queryFn: () => getPartners(typeFilter || undefined),
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Referral Partners</h1>
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {partnerTypes.map((type) => (
          <Button
            key={type.value}
            variant={typeFilter === type.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTypeFilter(type.value)}
          >
            {type.label}
          </Button>
        ))}
      </div>

      {partners && partners.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map((partner: any) => (
            <Card key={partner.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <HeartHandshake className="h-5 w-5" />
                  {partner.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      partner.partner_type === 'ngo'
                        ? 'bg-chart-3/15 text-chart-3'
                        : partner.partner_type === 'legal'
                        ? 'bg-primary/10 text-primary'
                        : partner.partner_type === 'medical'
                        ? 'bg-destructive/10 text-destructive'
                        : partner.partner_type === 'shelter'
                        ? 'bg-chart-5/15 text-chart-5'
                        : 'bg-secondary text-foreground/80'
                    }`}
                  >
                    {partner.partner_type}
                  </span>
                  {partner.available_24_7 && (
                    <span className="flex items-center gap-1 text-xs text-chart-3">
                      <Clock className="h-3 w-3" />
                      24/7
                    </span>
                  )}
                </div>

                <div className="text-sm space-y-1">
                  {partner.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <a href={`tel:${partner.phone}`} className="hover:underline">
                        {partner.phone}
                      </a>
                    </div>
                  )}
                  {partner.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${partner.email}`} className="hover:underline">
                        {partner.email}
                      </a>
                    </div>
                  )}
                </div>

                {partner.services_offered && partner.services_offered.length > 0 && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground mb-1">Services:</p>
                    <div className="flex flex-wrap gap-1">
                      {partner.services_offered.map((service: string) => (
                        <span
                          key={service}
                          className="px-2 py-0.5 bg-secondary text-muted-foreground text-xs rounded"
                        >
                          {service.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {partner.accepts_minors && (
                  <p className="text-xs text-chart-3">Accepts minors</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No referral partners found
          </CardContent>
        </Card>
      )}
    </div>
  )
}
